import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Table, Button, Badge, Row, Col, Form, Spinner, Alert, Modal, Pagination } from 'react-bootstrap';
import axios from 'axios';
import { useDebounce } from '../hooks/useDebounce'; // Um hook customizado para debounce
import { useAuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para os filtros
  const [filterClinica, setFilterClinica] = useState('');
  const [filterPerfil, setFilterPerfil] = useState('');
  const debouncedFilterClinica = useDebounce(filterClinica, 500);
  const debouncedFilterPerfil = useDebounce(filterPerfil, 500);

  const { startImpersonation } = useAuthContext();
  const navigate = useNavigate();

  // Estados para paginação
  const [pagination, setPagination] = useState(null);

  // Estados para os modais
  const [showResetModal, setShowResetModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [impersonatingUserId, setImpersonatingUserId] = useState(null); // <-- NOVO ESTADO

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/admin/users', {
        params: {
          page,
          clinica: debouncedFilterClinica,
          perfil: debouncedFilterPerfil,
        }
      });
      setUsers(response.data.data || []);
      setPagination({
        currentPage: response.data.current_page,
        totalPages: response.data.last_page,
      });
      setError(null);
    } catch (err) {
      setError('Não foi possível carregar a lista de usuários.');
      console.error("Erro ao buscar usuários:", err);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedFilterClinica, debouncedFilterPerfil]);

  useEffect(() => {
    fetchUsers(1); // Busca na primeira página sempre que os filtros mudam
  }, [fetchUsers]);

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    try {
      await axios.post(`/api/admin/users/${selectedUser.id}/reset-password`);
      setNewPassword('sent'); // Usamos um valor simbólico para indicar que o e-mail foi enviado
    } catch (error) {
      alert('Erro ao resetar a senha.');
      console.error(error);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedUser) return;
    try {
      await axios.post(`/api/admin/users/${selectedUser.id}/toggle-status`);
      setShowStatusModal(false);
      setSelectedUser(null);
      fetchUsers(); // Re-busca os usuários para atualizar a lista
    } catch (error) {
      alert('Erro ao alterar o status do usuário.');
      console.error(error);
    }
  };

  const handleImpersonate = async (user) => {
    setImpersonatingUserId(user.id); // Ativa o estado de carregamento para este botão
    try {
      const response = await axios.post(`/api/admin/users/${user.id}/impersonate`);
      // Espera a conclusão da atualização do estado de autenticação
      await startImpersonation(response.data);
      navigate('/dashboard', { replace: true }); // Navega para o dashboard APÓS a atualização
      // Não é necessário limpar o estado aqui, pois a página será desmontada
    } catch (error) {
      alert('Erro ao tentar personificar o usuário.');
      setImpersonatingUserId(null); // Limpa o estado de carregamento em caso de erro
    }
  };

  const getStatusBadge = (status) => {
    return status === 'Ativo' ? 'success' : 'danger';
  };

  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;
    let items = [];
    for (let number = 1; number <= pagination.totalPages; number++) {
      items.push(
        <Pagination.Item key={number} active={number === pagination.currentPage} onClick={() => fetchUsers(number)}>
          {number}
        </Pagination.Item>
      );
    }
    return <Pagination className="justify-content-center">{items}</Pagination>;
  };

  return (
    <Container fluid>
      <h1 className="mb-4">Gerenciamento de Usuários</h1>
      <Card className="mb-4">
        <Card.Header>Filtros</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group controlId="filterClinica">
                <Form.Label>Filtrar por Clínica</Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder="Digite o nome da clínica..."
                  value={filterClinica}
                  onChange={(e) => setFilterClinica(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="filterPerfil">
                <Form.Label>Filtrar por Perfil</Form.Label>
                <Form.Select value={filterPerfil} onChange={(e) => setFilterPerfil(e.target.value)}>
                  <option value="">Todos os Perfis</option>
                  <option value="medico">Médico</option>
                  <option value="recepcao">Recepção</option>
                  <option value="representante_legal">Representante Legal</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Lista de Todos os Usuários</Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Clínica</th>
                  <th>Perfil</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.nome}</td>
                    <td>{user.email}</td>
                    <td>{user.clinica}</td>
                    <td>{user.perfil}</td>
                    <td className="text-center"><Badge bg={getStatusBadge(user.status)}>{user.status}</Badge></td>
                    <td className="text-center">
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        className="me-2" 
                        title="Resetar Senha"
                        onClick={() => { setSelectedUser(user); setShowResetModal(true); }}
                      >
                        Resetar Senha
                      </Button>
                      <Button 
                        variant={user.status === 'Ativo' ? 'outline-danger' : 'outline-success'} 
                        size="sm" 
                        title={user.status === 'Ativo' ? 'Desativar Usuário' : 'Ativar Usuário'}
                        onClick={() => { setSelectedUser(user); setShowStatusModal(true); }}
                      >
                        {user.status === 'Ativo' ? 'Desativar' : 'Ativar'}
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        className="ms-2"
                        title="Personificar (logar como este usuário)"
                        onClick={() => handleImpersonate(user)}
                        disabled={impersonatingUserId === user.id}
                      >
                        {impersonatingUserId === user.id ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            <span className="visually-hidden">Personificando...</span>
                          </>
                        ) : (
                          'Personificar'
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {renderPagination()}
        </Card.Body>
      </Card>

      {/* Modal de Reset de Senha */}
      <Modal show={showResetModal} onHide={() => { setShowResetModal(false); setNewPassword(''); }}>
        <Modal.Header closeButton>
          <Modal.Title>Resetar Senha</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {newPassword ? (
            <Alert variant="success">
              Senha resetada com sucesso! Um e-mail com a nova senha foi enviado para <strong>{selectedUser?.nome}</strong>.
            </Alert>
          ) : (
            <p>Você tem certeza que deseja resetar a senha de <strong>{selectedUser?.nome}</strong>? Uma nova senha aleatória será gerada.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowResetModal(false); setNewPassword(''); }}>
            {newPassword ? 'Fechar' : 'Cancelar'}
          </Button>
          {!newPassword && (
            <Button variant="primary" onClick={handleResetPassword}>
              Sim, Resetar Senha
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal de Ativar/Desativar */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedUser?.status === 'Ativo' ? 'Desativar' : 'Ativar'} Usuário</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Você tem certeza que deseja <strong>{selectedUser?.status === 'Ativo' ? 'desativar' : 'ativar'}</strong> o usuário <strong>{selectedUser?.nome}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>Cancelar</Button>
          <Button variant={selectedUser?.status === 'Ativo' ? 'danger' : 'success'} onClick={handleToggleStatus}>Sim, {selectedUser?.status === 'Ativo' ? 'Desativar' : 'Ativar'}</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminUsersPage;